import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  alpha,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Security as SecurityIcon,
  CheckCircle as CheckIcon,
  Inventory as InventoryIcon,
  Person as PersonIcon,
} from '@mui/icons-material';

const EPP = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);

  const eppItems = [
    { nombre: 'Casco de Seguridad', stock: 45, minimo: 20, estado: 'OK' },
    { nombre: 'Lentes de Seguridad', stock: 120, minimo: 50, estado: 'OK' },
    { nombre: 'Guantes de Cuero', stock: 85, minimo: 30, estado: 'OK' },
    { nombre: 'Botas de Seguridad', stock: 12, minimo: 15, estado: 'Bajo' },
    { nombre: 'Chaleco Reflectivo', stock: 65, minimo: 25, estado: 'OK' },
    { nombre: 'Protector Auditivo', stock: 95, minimo: 40, estado: 'OK' },
    { nombre: 'Ropa de Trabajo FR', stock: 8, minimo: 20, estado: 'Crítico' },
    { nombre: 'Arnés de Seguridad', stock: 25, minimo: 10, estado: 'OK' },
  ];

  const entregasRecientes = [
    { id: 1, empleado: 'Juan Rodríguez', legajo: 'PL-001', items: ['Casco', 'Guantes', 'Lentes'], fecha: '01/02/2026', firmado: true },
    { id: 2, empleado: 'Pedro García', legajo: 'PL-002', items: ['Botas', 'Chaleco'], fecha: '30/01/2026', firmado: true },
    { id: 3, empleado: 'Carlos Mendoza', legajo: 'PL-004', items: ['Casco', 'Arnés'], fecha: '28/01/2026', firmado: true },
    { id: 4, empleado: 'Ana Martínez', legajo: 'PL-005', items: ['Guantes', 'Lentes', 'Protector Auditivo'], fecha: '25/01/2026', firmado: false },
  ];

  const resumenStats = [
    { label: 'Items en Stock', value: 455, icon: <InventoryIcon />, color: theme.palette.primary.main },
    { label: 'Entregas del Mes', value: 32, icon: <PersonIcon />, color: theme.palette.success.main },
    { label: 'Stock Bajo', value: 2, icon: <SecurityIcon />, color: theme.palette.warning.main },
    { label: 'Pendientes Firma', value: 1, icon: <CheckIcon />, color: theme.palette.info.main },
  ];

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Equipos de Protección Personal
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Control de entrega y stock de EPP
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
          Nueva Entrega
        </Button>
      </Box>

      {/* Resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {resumenStats.map((stat, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: alpha(stat.color, 0.1), color: stat.color }}>
                    {stat.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight={700} color={stat.color}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Stock EPP */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Stock de EPP
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell align="center">Stock</TableCell>
                      <TableCell align="center">Mínimo</TableCell>
                      <TableCell align="center">Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {eppItems.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.nombre}</TableCell>
                        <TableCell align="center">
                          <Typography 
                            variant="body2" 
                            fontWeight={600}
                            color={item.estado === 'Crítico' ? 'error.main' : item.estado === 'Bajo' ? 'warning.main' : 'text.primary'}
                          >
                            {item.stock}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{item.minimo}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={item.estado}
                            size="small"
                            color={item.estado === 'OK' ? 'success' : item.estado === 'Bajo' ? 'warning' : 'error'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Entregas Recientes */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Entregas Recientes
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Empleado</TableCell>
                      <TableCell>Items Entregados</TableCell>
                      <TableCell>Fecha</TableCell>
                      <TableCell align="center">Firmado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entregasRecientes.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, bgcolor: theme.palette.primary.main, fontSize: '0.7rem' }}>
                              {row.empleado.split(' ').map(n => n[0]).join('')}
                            </Avatar>
                            <Box>
                              <Typography variant="body2">{row.empleado}</Typography>
                              <Typography variant="caption" color="text.secondary">{row.legajo}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {row.items.map((item, i) => (
                              <Chip key={i} label={item} size="small" variant="outlined" />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>{row.fecha}</TableCell>
                        <TableCell align="center">
                          {row.firmado ? (
                            <CheckIcon color="success" />
                          ) : (
                            <Button size="small" variant="outlined" color="warning">
                              Pendiente
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog Nueva Entrega */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon color="primary" />
            Registrar Entrega de EPP
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Empleado</InputLabel>
                <Select label="Empleado">
                  <MenuItem value="1">Juan Rodríguez - PL-001</MenuItem>
                  <MenuItem value="2">Pedro García - PL-002</MenuItem>
                  <MenuItem value="3">María López - PL-003</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Items a Entregar</InputLabel>
                <Select label="Items a Entregar" multiple defaultValue={[]}>
                  <MenuItem value="casco">Casco de Seguridad</MenuItem>
                  <MenuItem value="lentes">Lentes de Seguridad</MenuItem>
                  <MenuItem value="guantes">Guantes de Cuero</MenuItem>
                  <MenuItem value="botas">Botas de Seguridad</MenuItem>
                  <MenuItem value="chaleco">Chaleco Reflectivo</MenuItem>
                  <MenuItem value="protector">Protector Auditivo</MenuItem>
                  <MenuItem value="ropa">Ropa de Trabajo FR</MenuItem>
                  <MenuItem value="arnes">Arnés de Seguridad</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fecha de Entrega"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Motivo</InputLabel>
                <Select label="Motivo">
                  <MenuItem value="nuevo">Dotación Nueva</MenuItem>
                  <MenuItem value="reposicion">Reposición</MenuItem>
                  <MenuItem value="desgaste">Desgaste</MenuItem>
                  <MenuItem value="perdida">Pérdida</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Observaciones" multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => setOpenDialog(false)}>
            Registrar Entrega
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EPP;
